import type { AppointmentSuggestedSlots } from "@/types/appointment"
import type { Office } from "@/types/offices"
import type { Patient } from "@/types/patient"
import dayjs from "@/utils/dayjs"

/** Palette fissa per distinguere le sedi nel calendario (cicla oltre la lunghezza). */
export const CALENDAR_OFFICE_COLORS = [
	"var(--primary-400-color)",
	"#52C9C0",
	"#EE86B5",
	"#9B7EDE",
	"#F5A623",
	"#6BCB77",
	"#E07A5F",
	"#4D96FF",
	"#7B8CDE",
	"#9C6644"
] as const

export const CALENDAR_DRAFT_COLOR = "#9CA3AF"
export const CALENDAR_EVENT_CONTRAST_COLOR = "#FFFFFF"
export const MINUTES_PER_DAY = 1440
export const CALENDAR_DEFAULT_DAY_START_HOUR = 9

export interface CalendarAppointmentEventColors {
	color: string
	contrastColor: string
}

export function getCalendarOfficeColorByIndex(index: number): CalendarOfficeColor {
	const paletteIndex =
		((index % CALENDAR_OFFICE_COLORS.length) + CALENDAR_OFFICE_COLORS.length) % CALENDAR_OFFICE_COLORS.length

	return CALENDAR_OFFICE_COLORS[paletteIndex]!
}

export function getCalendarOfficeIndex(officeId: number, offices: readonly Pick<Office, "id">[]): number {
	const index = offices.findIndex(office => office.id === officeId)

	if (index !== -1) {
		return index
	}

	return officeId % CALENDAR_OFFICE_COLORS.length
}

export function getCalendarOfficeColor(officeId: number, offices: readonly Pick<Office, "id">[]): CalendarOfficeColor {
	return getCalendarOfficeColorByIndex(getCalendarOfficeIndex(officeId, offices))
}

export type CalendarOfficeColor = (typeof CALENDAR_OFFICE_COLORS)[number]

export function getCalendarAppointmentTitle(
	appointment: {
		title: string
		draft: boolean
		patient: Pick<Patient, "name" | "surname"> | null
	},
	draftLabel?: string
) {
	const title = appointment.title?.trim() ?? ""
	const patientName = appointment.patient ? `${appointment.patient.name} ${appointment.patient.surname}`.trim() : ""

	// Join patient and title, dropping the title when it's empty (e.g. an appointment identified only by the patient).
	const label = [patientName, title].filter(Boolean).join("  •  ")

	if (appointment.draft && draftLabel && label) {
		return `${label} (${draftLabel})`
	}

	return label
}

export function getCalendarAppointmentEventColors(
	appointment: { draft: boolean; office: Pick<Office, "id"> | null },
	offices: readonly Pick<Office, "id">[]
): CalendarAppointmentEventColors {
	if (appointment.draft || !appointment.office) {
		return {
			color: CALENDAR_DRAFT_COLOR,
			contrastColor: CALENDAR_EVENT_CONTRAST_COLOR
		}
	}

	return {
		color: getCalendarOfficeColor(appointment.office.id, offices),
		contrastColor: CALENDAR_EVENT_CONTRAST_COLOR
	}
}

/** Timestamp at the start of the day containing `value`. */
export function startOfDayMs(value: dayjs.ConfigType) {
	return dayjs(value).startOf("day").valueOf()
}

/**
 * All-day appointments encode their span in `duration` as whole days.
 * These two helpers convert between that duration and a day count (never below one day).
 */
export function allDayDurationToDayCount(durationMinutes: number) {
	return Math.max(Math.round(durationMinutes / MINUTES_PER_DAY), 1)
}

export function dayCountToAllDayDuration(dayCount: number) {
	return Math.max(dayCount, 1) * MINUTES_PER_DAY
}

export function getAllDayEventRange(appointment: { datetime: string; duration: number }) {
	const start = dayjs(appointment.datetime, "YYYY-MM-DD HH:mm:ss").startOf("day")

	// FullCalendar all-day events use an exclusive end, so it sits `dayCount` days after the start.
	return {
		start: start.format("YYYY-MM-DD"),
		end: start.add(allDayDurationToDayCount(appointment.duration), "day").format("YYYY-MM-DD")
	}
}

/**
 * Pre-formats suggested slots for display: drops empty days and renders the weekday, date,
 * and per-slot time labels once (so templates don't re-parse each date on every render).
 */
export function formatSuggestedDays(days: readonly AppointmentSuggestedSlots[], locale: string) {
	return days
		.filter(day => day.slots.length)
		.map(day => {
			const date = dayjs(day.date, "YYYY-MM-DD").locale(locale)

			return {
				date: day.date,
				weekday: date.format("ddd"),
				label: date.format("D MMM"),
				slots: day.slots.map(slot => ({
					...slot,
					fromLabel: dayjs(slot.from, "YYYY-MM-DD HH:mm:ss").format("HH:mm"),
					toLabel: dayjs(slot.to, "YYYY-MM-DD HH:mm:ss").format("HH:mm")
				}))
			}
		})
}

export function getFirstAvailableDatetimeForDay(
	day: Date,
	appointments: readonly { datetime: string; duration: number; all_day: boolean }[],
	durationMinutes: number,
	options?: { startHour?: number }
) {
	const startHour = options?.startHour ?? CALENDAR_DEFAULT_DAY_START_HOUR
	const dayAnchor = dayjs(day).startOf("day")
	let candidate = dayAnchor.hour(startHour).minute(0).second(0).millisecond(0)
	const now = dayjs()

	if (dayAnchor.isSame(now, "day") && now.isAfter(candidate)) {
		candidate = now.startOf("minute")
	}

	const dayIntervals = appointments
		.map(appointment => {
			const start = dayjs(appointment.datetime, "YYYY-MM-DD HH:mm:ss")

			if (!start.isSame(dayAnchor, "day") || appointment.all_day) {
				return null
			}

			return {
				start,
				end: start.add(appointment.duration, "minute")
			}
		})
		.filter((interval): interval is { start: dayjs.Dayjs; end: dayjs.Dayjs } => interval !== null)
		.sort((a, b) => a.start.valueOf() - b.start.valueOf())

	for (const { start, end } of dayIntervals) {
		if (!end.isAfter(candidate)) {
			continue
		}

		const candidateEnd = candidate.add(durationMinutes, "minute")

		if (candidateEnd.isSame(start) || candidateEnd.isBefore(start)) {
			break
		}

		candidate = end
	}

	return candidate.valueOf()
}
